import { ChangeEvent, FC, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { TopBar } from "components/navigation/TopBar"
import { SearchInput } from "components/search/SearchInput"
import { Table } from "components/tables/Table"

import { ModalCreateUser } from "features/user/actions/ModalCreateUser/ModalCreateUser"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { URL } from "constants/api/requests"
import { countries } from "constants/countries"
import { tableAdminsColumns } from "constants/data"
import { TableColumnsInterface } from "constants/interfaces"
import { PERMISSIONS } from "constants/permissions"
import { adminRoles, allRolesFormatted, formatRoleName } from "constants/roles"

import { RoleLookup, roleManagementService } from "services/role-management/role-management.service"
import { BUILTIN_ROLE_IDS } from "services/user/type"
import { userService } from "services/user/user.service"

export const Admins: FC = () => {
  const { user } = useProps()
  const { has } = usePermissions()
  const { t } = useTranslation(["translation"], { useSuspense: false })

  const [query, setQuery] = useState<string>("")
  const [tableUpdatedAt] = useState<Date | number>(Date.now())
  const [country, setCountry] = useState<string | null>(null)
  const [role, setRole] = useState<string | null>(null)
  const [openCreateUser, setOpenCreateUser] = useState(false)
  const [allRoles, setAllRoles] = useState<RoleLookup[]>([])

  useEffect(() => {
    roleManagementService
      .listRolesLookup()
      .then((rs) => rs && setAllRoles(rs))
      .catch(() => {
        // Non-fatal — fall back to the static built-in list rendered below.
      })
  }, [])

  // Role pickers used across this page:
  // - inline `role` column dropdown (built from `roleOptions`)
  // - the "Filter by role" dropdown above the table (built from `roleFilterOptions`)
  // Both prefer the fetched list of all roles, and fall back to the static
  // built-in list when the fetch fails. ADMIN can only be assigned by ADMIN.
  const roleOptions = useMemo(() => {
    const isAdminCaller = user?.role === BUILTIN_ROLE_IDS.ADMIN
    if (allRoles.length > 0) {
      return allRoles.map((r) => ({
        name: r.is_builtin ? formatRoleName(r.id) : `${r.name} (custom)`,
        value: r.id,
        disable: r.id === BUILTIN_ROLE_IDS.ADMIN && !isAdminCaller
      }))
    }
    return allRolesFormatted.map((opt) => ({
      ...opt,
      disable: opt.value === BUILTIN_ROLE_IDS.ADMIN && !isAdminCaller
    }))
  }, [allRoles, user?.role])

  const roleFilterOptions = useMemo(() => {
    if (allRoles.length > 0) {
      return allRoles.map((r) => ({
        name: r.is_builtin ? formatRoleName(r.id) : `${r.name} (custom)`,
        value: r.id
      }))
    }
    return adminRoles
  }, [allRoles])

  const columns = useMemo<TableColumnsInterface[]>(
    () =>
      tableAdminsColumns.map((col) =>
        col.value === "role" ? { ...col, dropdownOptions: roleOptions } : col
      ),
    [roleOptions]
  )

  const onUserClick = (row: any): void => {
    if (row?.id) {
      window.open(URL.USER.replace(":id", row.id), "_blank")
    }
  }

  const handleDropdownRoleChange = async (row: any, column: TableColumnsInterface, newValue: string) => {
    if (column.value === "role") {
      try {
        await userService.editUser(row.id, { role_id: newValue })
        toast.success("Role updated")
      } catch (error) {
        toast.error("Failed to update role")
        console.error(error)
      }
    }
  }

  return (
    <div className="grid w-full justify-start gap-8 p-4 pt-0 sm:p-8 sm:pt-0 grid-col-1">
      <TopBar title={t("pages.admins.title")} />
      <WelcomeSection />
      <div className="flex gap-4 z-20 flex-wrap">
        <div className="max-w-sm">
          <SearchInput placeholder={t("form.searchUsernameEmail")} onChange={setQuery} />
        </div>

        <div className="text-wrap max-w-sm shrink">
          <DropdownMenu
            placeholder={t("form.allCountries")}
            options={countries}
            value={country}
            onChange={(ev: ChangeEvent<HTMLSelectElement>) => setCountry(ev.target.value)}
            rounded
          />
        </div>

        <div className="text-wrap max-w-md">
          <DropdownMenu
            placeholder={t("form.allRoles")}
            options={roleFilterOptions}
            value={role}
            onChange={(ev: ChangeEvent<HTMLSelectElement>) => setRole(ev.target.value)}
            rounded
          />
        </div>
        <button
          className="ml-auto btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
          onClick={() => setOpenCreateUser(true)}
        >
          {t("common.create")}
        </button>
      </div>
      <Table
        title={t("user.users")}
        handler={userService.getAdminsTable}
        handleDropdownChange={handleDropdownRoleChange}
        columns={columns}
        filters={{ q: query, country, role }}
        onRowClick={onUserClick}
        updatedAt={tableUpdatedAt}
        isExport={!!user && has(PERMISSIONS.PAGE_ADMINS)}
        isColumnsFilterable
      />
      <ModalCreateUser open={openCreateUser} handler={() => setOpenCreateUser(false)} />
    </div>
  )
}
